import apiClient from './apiClient';

/**
 * Service for handling statistics-related apiClient calls
 */
const statisticsService = {
  /**
   * Get statistics based on a period or custom date range
   * @param {string} period - The period ('day', 'week', 'month', 'year')
   * @param {string} startDate - Custom start date in YYYY-MM-DD format
   * @param {string} endDate - Custom end date in YYYY-MM-DD format
   * @returns {Promise<Object>} - The statistics data
   */
  async getStatistics(period = 'month', startDate = null, endDate = null) {
    try {
      let url = '/statistics/analytics/?';
      
      if (startDate && endDate) {
        url += `start_date=${startDate}&end_date=${endDate}`;
      } else if (period) {
        url += `period=${period}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  },

  /**
   * Top productos por unidades vendidas (action del OrderViewSet).
   * Prueba /orders/… (ruta habitual) y /products/orders/… por si el router anida el viewset.
   */
  async getTopSellingProducts(period = 'month', startDate = null, endDate = null) {
    const query =
      startDate && endDate
        ? `start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
        : `period=${encodeURIComponent(period || 'month')}`;
    const candidates = [
      `/orders/top-selling-products/?${query}`,
      `/products/orders/top-selling-products/?${query}`,
    ];
    let lastError;
    for (const url of candidates) {
      try {
        const response = await apiClient.get(url);
        return response.data;
      } catch (error) {
        lastError = error;
        if (error.response?.status === 404) continue;
        console.error('Error fetching top selling products:', error);
        throw error;
      }
    }
    console.error('Error fetching top selling products (404 en todas las rutas):', lastError);
    throw lastError;
  },
  
  /**
   * Get detailed customer statistics
   * @returns {Promise<Object>} - Customer statistics data
   */
  async getCustomerStats() {
    try {
      const response = await apiClient.get('/statistics/customers/');
      return response.data;
    } catch (error) {
      console.error('Error fetching customer statistics:', error);
      throw error;
    }
  },

  /**
   * Get visitor statistics
   * @param {string} period - The period ('day', 'week', 'month', 'year')
   * @param {string} startDate - Custom start date in YYYY-MM-DD format
   * @param {string} endDate - Custom end date in YYYY-MM-DD format
   * @returns {Promise<Object>} - Visitor statistics data
   */
  async getVisitorStats(period = null, startDate = null, endDate = null) {
    try {
      let url = '/visitors/stats/';
      
      // Add query parameters if provided
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else if (period) {
        url += `?period=${period}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching visitor statistics:', error);
      throw error;
    }
  },

  /**
   * Get user registration statistics
   * @param {string} period - The period ('day', 'week', 'month', 'year')
   * @param {string} startDate - Custom start date in YYYY-MM-DD format
   * @param {string} endDate - Custom end date in YYYY-MM-DD format
   * @returns {Promise<Object>} - User registration statistics data
   */
  async getUserStats(period = null, startDate = null, endDate = null) {
    try {
      let url = '/statistics/users/';
      
      // Add query parameters if provided
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else if (period) {
        url += `?period=${period}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      throw error;
    }
  }
};

export default statisticsService;